import type { CSSProperties } from 'react';
import { styled, Tabs } from '@mui/material';

export const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  backgroundColor: 'transparent',
});

export const SheetScrollArea = styled('div')({
  flex: 1,
  overflow: 'auto',
  width: '100%',
  position: 'relative',
  padding: '20px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  backgroundColor: 'transparent',
});

export const SheetContent = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[8],
  border: `1px solid ${theme.palette.divider}`,
  width: 'fit-content',
  minWidth: '100px',
  minHeight: '100px',
  boxSizing: 'border-box',
}));

export const OverlayImage = styled('img')<{ $x: number; $y: number; $w: number; $h: number }>(({ $x, $y, $w, $h }) => ({
  position: 'absolute',
  zIndex: 10,
  objectFit: 'contain',
  pointerEvents: 'none',
  left: `${$x}px`,
  top: `${$y}px`,
  width: `${$w}px`,
  height: `${$h}px`,
}));

export const StyledTable = styled('table')<{ $totalWidth: number }>(({ $totalWidth, theme }) => ({
  width: $totalWidth,
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  fontSize: '12px',
  fontFamily: 'Calibri, Arial, sans-serif',
  color: theme.palette.common.black,
}));

export const StyledCol = styled('col')<{ $width: number }>(({ $width }) => ({
  width: `${$width}px`,
}));

export const StyledTr = styled('tr')<{ $height: number }>(({ $height }) => ({
  height: `${$height}px`,
}));

interface StyledTdProps {
  $bgColor: string;
  $textAlign: CSSProperties['textAlign'];
  $verticalAlign: CSSProperties['verticalAlign'];
  $isBold: boolean;
  $fontColor: string;
  $hasBorderTop: boolean;
}

export const StyledTd = styled('td')<StyledTdProps>(({ theme, $bgColor, $textAlign, $verticalAlign, $isBold, $fontColor, $hasBorderTop }) => ({
  padding: '2px 4px',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  boxSizing: 'border-box',
  lineHeight: 'normal',
  backgroundColor: $bgColor,
  textAlign: $textAlign,
  verticalAlign: $verticalAlign,
  fontWeight: $isBold ? 'bold' : 'normal',
  color: $fontColor,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderTop: $hasBorderTop ? `1px solid ${theme.palette.common.black}` : `1px solid ${theme.palette.divider}`,
}));

export const Footer = styled('div')({
  height: '40px',
  backgroundColor: '#1e1e1e',
  borderTop: '1px solid #333',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  zIndex: 20,
});

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: '40px',
  '& .MuiTab-root': {
    minHeight: '40px',
    textTransform: 'none',
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.7)',
    padding: '0 15px',
    fontSize: '13px',
  },
  '& .Mui-selected': {
    color: `${theme.palette.primary.light} !important`,
    borderTop: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: '#2a2a2a',
  },
  '& .MuiTabs-indicator': { display: 'none' },
}));

export const LoadingBox = styled('div')({
  padding: '32px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  color: '#fff',
});

export const ErrorBox = styled('div')({ padding: '32px', textAlign: 'center', color: '#fff' });
